import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Header.module.css';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Added React refs for the mobile menu and menu toggle button
  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);

  /* Mobile menu closes automatically after the route or hash changes. */
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Handle Escape key to close menu
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Added body scroll locking while the mobile menu is open & Move focus inside mobile menu when it opens.
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Move focus inside the menu for screen readers
      if (menuRef.current) {
        menuRef.current.focus();
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Common function to close menu and return focus to the toggle button
  const closeMenu = () => {
    setIsMenuOpen(false);
    if (toggleButtonRef.current) {
      toggleButtonRef.current.focus();
    }
  };

  const getStickyOffset = () => {
    const stickyNavbar = document.querySelector('.navbar.fixed-top');
    return stickyNavbar ? stickyNavbar.getBoundingClientRect().height + 16 : 16;
  };

  const focusTarget = (targetElement) => {
    if (!targetElement) {
      return;
    }

    if (!targetElement.hasAttribute('tabindex')) {
      targetElement.setAttribute('tabindex', '-1');
    }

    targetElement.focus({ preventScroll: true });
  };

  const scrollToSelector = (selector) => {
    const targetElement = document.querySelector(selector);

    if (!targetElement) {
      return false;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targetTop = window.scrollY + targetElement.getBoundingClientRect().top - getStickyOffset();

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });

    window.setTimeout(() => {
      focusTarget(targetElement);
    }, prefersReducedMotion ? 0 : 260);

    return true;
  };

  const handleSectionLink = (event, selector) => {
    if (window.location.pathname !== '/') {
      return;
    }

    event.preventDefault();

    const didScroll = scrollToSelector(selector);

    if (didScroll) {
      window.history.replaceState(null, '', `/${selector}`);
    }
  };

  return (
    <>
      <header className={styles.headerRoot}>
        <nav className="navbar navbar-expand-lg py-3 fixed-top bg-light" aria-label="Primary" data-testid="primary-navigation">
          <div className="container">
            <Link className={styles.brand} to="/" aria-label="Our Broking home">
              <img className={styles.brandLogo} src="/img/logo.gif" alt="" width="60" height="60" />
              <span>OUR BROKING</span>
            </Link>

            <button
              ref={toggleButtonRef}
              className={`navbar-toggler ${styles.navbarToggler}`}
              type="button"
              aria-controls="primary-navigation-menu"
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span className="navbar-toggler-icon" aria-hidden="true"></span>
            </button>

            <div
              ref={menuRef}
              id="primary-navigation-menu"
              tabIndex="-1"
              className={`navbar-collapse ${isMenuOpen ? styles.menuOpen : styles.menuClosed}`}
            >
              <ul className="navbar-nav ms-auto align-items-lg-center">
                <li className="nav-item px-lg-3">
                  <a
                    className={`nav-link home-link ${styles.navLink}`}
                    href="/#home"
                    onClick={(event) => {
                      handleSectionLink(event, '#home');
                      closeMenu();
                    }}
                  >
                    Home
                  </a>
                </li>
                <li className="nav-item px-lg-3">
                  <a
                    className={`nav-link services-link ${styles.navLink}`}
                    href="/#services"
                    onClick={(event) => {
                      handleSectionLink(event, '#services');
                      closeMenu();
                    }}
                  >
                    Product &amp; Services
                  </a>
                </li>
                <li className="nav-item px-lg-3">
                  <a
                    className={`nav-link about-link ${styles.navLink}`}
                    href="/#about"
                    onClick={(event) => {
                      handleSectionLink(event, '#about');
                      closeMenu();
                    }}
                  >
                    About Us
                  </a>
                </li>
                <li className="nav-item px-lg-3">
                  <Link
                    className={`nav-link contact-link ${styles.navLink}`}
                    to="/contact-us"
                    onClick={closeMenu}
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>

              <a
                href="https://backoffice.ourbroking.com/Account/Login"
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-warning ms-lg-3 px-4 ${styles.headerCta}`}
                onClick={closeMenu}
              >
                Join Us
                <span className={styles.srOnly}> opens in a new tab</span>
              </a>
            </div>
          </div>
        </nav>
        
        {/* Added mobile menu backdrop styling and structure for outside-click dismissal. */}
        {isMenuOpen && (
          <div 
            className={styles.menuBackdrop} 
            onClick={closeMenu} 
            aria-hidden="true" 
          />
        )}
      </header>
    </>
  );
}

export default Header;